import { http } from "@/utils/http";
import type {
  Review,
  ReviewsResponse,
  ReviewStatsResponse,
  SubmitReviewRequest,
  ReviewSearchRequest,
} from "@/types";

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const reviewsApi = {
  // Get course reviews
  async getCourseReviews(
    courseId: number,
    page = 0,
    size = 10,
  ): Promise<ReviewsResponse> {
    const response = await http.get<ApiResponse<ReviewsResponse>>(
      `/api/v1/reviews/course/${courseId}`,
      {
        params: { page, size },
      },
    );
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Get course review statistics
  async getCourseStats(courseId: number): Promise<ReviewStatsResponse> {
    const response = await http.get<ApiResponse<ReviewStatsResponse>>(
      `/api/v1/reviews/course/${courseId}/stats`,
    );
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Submit a new review
  async submitReview(reviewData: SubmitReviewRequest): Promise<Review> {
    const response = await http.post<ApiResponse<Review>>("/api/v1/reviews", reviewData);
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Get current user's reviews
  async getUserReviews(page = 0, size = 10): Promise<ReviewsResponse> {
    const response = await http.get<ApiResponse<ReviewsResponse>>("/api/v1/reviews/my", {
      params: { page, size },
    });
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Update user's review
  async updateReview(
    reviewId: string,
    reviewData: SubmitReviewRequest,
  ): Promise<Review> {
    const response = await http.put<ApiResponse<Review>>(
      `/api/v1/reviews/${reviewId}`,
      reviewData,
    );
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Delete user's review
  async deleteReview(reviewId: string): Promise<void> {
    await http.delete(`/api/v1/reviews/${reviewId}`);
  },

  // Advanced review search
  async searchReviews(
    searchParams: ReviewSearchRequest,
  ): Promise<ReviewsResponse> {
    const response = await http.post<ApiResponse<ReviewsResponse>>(
      "/api/v1/reviews/search",
      searchParams,
    );
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Get global review statistics
  async getGlobalStats(): Promise<any> {
    const response = await http.get<ApiResponse<any>>("/api/v1/reviews/stats/global");
    return response.data.data; // Extract data from ApiResponse wrapper
  },

  // Record review view
  async recordReviewView(reviewId: string): Promise<{
    recorded: boolean;
    viewCount: number;
    canView: boolean;
  }> {
    const response = await http.post<{
      recorded: boolean;
      viewCount: number;
      canView: boolean;
    }>(`/api/v1/reviews/${reviewId}/view`);
    return response.data;
  },

  // Get review view status
  async getReviewViewStatus(): Promise<{
    viewCount: number;
    viewedReviewIds: string[];
    limit: number;
  }> {
    const response = await http.get<{
      viewCount: number;
      viewedReviewIds: string[];
      limit: number;
    }>("/api/v1/reviews/view-status");
    return response.data;
  },
};
